import * as R from 'ramda';
import { IndicatorScore } from 'types/api/envScales/IndicatorScore';
import { Dimension } from 'types/api/envScales/Dimension';
import messages from './messages';
import i18n from 'helpers/i18n';

export type IndicatorScoreValues = -1 | 0 | 50 | 100 | null;

export type IndicatorScoreState = {
  [key: number]: IndicatorScoreValues;
};

export type IndicatorScorePayload = {
  indicator_id: number;
  score: IndicatorScoreValues;
};

export function mapIndicatorScores(
  scores: IndicatorScoreState
): IndicatorScorePayload[] {
  let scoresPayload: IndicatorScorePayload[] = [];

  R.forEachObjIndexed((value, key) => {
    if (value !== null) {
      scoresPayload = [...scoresPayload, { indicator_id: key, score: value }];
    }
  }, scores);

  return scoresPayload;
}

export function getInitialIndicators(
  scores: IndicatorScore[]
): IndicatorScoreState {
  let state: IndicatorScoreState = {};

  R.forEach(score => {
    state[score.indicator_id] = score.score;
  }, scores);

  return state;
}

export function calculateSuggestions(
  indicatorsComplete: boolean,
  indicatorScores: IndicatorScorePayload[]
) {
  if (!indicatorsComplete) {
    return [];
  }

  const scoresArray = indicatorScores.map(indScore => {
    switch (indScore.score) {
      case 100:
        return 7;
      case 50:
        return 4;
      default:
        return 1;
    }
  });

  const MIN_SCORE = 1;
  const MAX_SCORE = 7;
  const avg = R.mean(scoresArray);
  const isInteger = avg % 1 === 0;
  let scoreSuggestions: number[] = [];

  if (isInteger) {
    scoreSuggestions = [avg - 1, avg, avg + 1];
  } else {
    scoreSuggestions = [Math.floor(avg), Math.ceil(avg)];
  }

  return scoreSuggestions.filter(s => s >= MIN_SCORE && s <= MAX_SCORE);
}

export function getScoreWarnings(
  scoreValue: number,
  dimension: Dimension
): string[] {
  let warnings: Array<string> = [];

  const uncommonBelow3 = ['pc', 'ts', 'rsp', 'bm', 'pr', 'ilf'];
  const uncommonAbove4 = ['cd', 'qf'];

  if (scoreValue < 3 && uncommonBelow3.includes(dimension.abbreviation)) {
    warnings.push(
      i18n.ft(messages.warnings.uncommonBelow, {
        number: 3,
        dimension: dimension.name
      })
    );
  } else if (
    scoreValue > 4 &&
    uncommonAbove4.includes(dimension.abbreviation)
  ) {
    warnings.push(
      i18n.ft(messages.warnings.uncommonAbove, {
        number: 4,
        dimension: dimension.name
      })
    );
  } else if (scoreValue > 3 && dimension.abbreviation === 'nc') {
    warnings.push(
      i18n.ft(messages.warnings.uncommonAbove, {
        number: 3,
        dimension: dimension.name
      })
    );
  }

  return warnings;
}

export function getInformalScoreWarnings(
  scoreValue: number,
  dimension: Dimension
): string[] {
  let warnings: Array<string> = [];

  if (dimension.abbreviation === 'nc' && scoreValue > 2) {
    warnings.push(
      i18n.ft(messages.warnings.informalAboveLow, {
        dimension: dimension.name
      })
    );
  }

  if (dimension.abbreviation === 'cd' && scoreValue > 5) {
    warnings.push(
      i18n.ft(messages.warnings.informalAboveMid, {
        dimension: dimension.name
      })
    );
  }

  return warnings;
}
